import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IInventoryAllocationsState } from '../reducers/inventory-allocations.reducer';
import { InventoryAllocation } from '../../interfaces/allocation-group';
import { isEmptyArray } from '../../shared/utils';

export const selectInventoryAllocationsState =
  createFeatureSelector<IInventoryAllocationsState>('inventoryAllocations');

// User allocation groups
export const selectUserAllocationGroups = createSelector(
  selectInventoryAllocationsState,
  (state: IInventoryAllocationsState) => state.userAllocationGroups,
);

export const selectUserAllocationGroupsLoaded = createSelector(
  selectInventoryAllocationsState,
  (state: IInventoryAllocationsState) => state.userAllocationGroupsLoaded,
);

export const selectIsFetchingUserAllocationGroups = createSelector(
  selectInventoryAllocationsState,
  (state: IInventoryAllocationsState) => state.fetchingUserAllocationGroups,
);

export const selectHasUserAllocationGroups = createSelector(
  selectUserAllocationGroups,
  (groups) => groups.length > 0,
);

// Product allocations (parameterized by productId)
export const selectProductAllocations = (productId: number) =>
  createSelector(
    selectInventoryAllocationsState,
    (state: IInventoryAllocationsState) => state.byProductId[productId]?.allocations || [],
  );

export const selectProductAllocationsLoaded = (productId: number) =>
  createSelector(
    selectInventoryAllocationsState,
    (state: IInventoryAllocationsState) => state.byProductId[productId]?.loaded ?? false,
  );

export const selectProductAllocationsFailed = (productId: number) =>
  createSelector(
    selectInventoryAllocationsState,
    (state: IInventoryAllocationsState) => state.byProductId[productId]?.error ?? false,
  );

export const selectIsFetchingProductAllocations = (productId: number) =>
  createSelector(
    selectInventoryAllocationsState,
    (state: IInventoryAllocationsState) => state.byProductId[productId]?.fetching ?? false,
  );

/**
 * The products of a batch that are worth asking for. The list view pages products in as the user
 * scrolls and hands the whole grown array back every time, so without this every page would
 * re-request everything above it. A product already in flight is skipped for the same reason.
 */
export const selectProductIdsNeedingAllocations = (productIds: number[]) =>
  createSelector(
    selectInventoryAllocationsState,
    (state: IInventoryAllocationsState) => {
      const unique = Array.from(new Set(productIds || []));
      return unique.filter((productId) => {
        const entry = state.byProductId[productId];
        // A product whose last request failed is left alone: the products setter fires on every
        // order item save as well as on paging, so retrying here would turn a broken endpoint into
        // a request per keystroke-ish event. Recovery comes from a fresh page of ids or a reload.
        return !entry || (!entry.loaded && !entry.fetching && !entry.error);
      });
    },
  );

// Mirror ProductService public methods, now parameterized by productId

export const selectInventoryAllocationForSku = (productId: number, skuId?: number) =>
  createSelector(
    selectProductAllocations(productId),
    (allocations): InventoryAllocation => {
      const fallback: InventoryAllocation = { sku_id: skuId, in_stock: false, allocation_qty: [] };
      if (isEmptyArray(allocations)) {
        return fallback;
      }
      const result = skuId ? allocations.find((a) => a.sku_id === skuId) : allocations[0];
      return result || fallback;
    },
  );

export const selectAllProductAllocationsDefined = (productId: number) =>
  createSelector(
    selectProductAllocations(productId),
    (allocations) => {
      if (isEmptyArray(allocations)) {
        return false;
      }
      const onlyInStock = allocations.filter((a) => a.in_stock === true);
      return onlyInStock.every((a) => !isEmptyArray(a.allocation_qty));
    },
  );

export const selectTotalAvailableQuantity = (productId: number, skuId?: number) =>
  createSelector(
    selectProductAllocations(productId),
    (allocations): { inStock: boolean; totalQty: number } | null => {
      if (isEmptyArray(allocations)) {
        return null;
      }
      const skuInventory = skuId ? allocations.find((a) => a.sku_id === skuId) : allocations[0];
      if (!skuInventory || isEmptyArray(skuInventory.allocation_qty)) {
        return null;
      }
      const totalQty = skuInventory.allocation_qty.reduce((sum, aq) => sum + aq.available_qty, 0);
      return { inStock: skuInventory.in_stock, totalQty };
    },
  );

export const selectSomeVariationsHasAllocationGroups = (productId: number) =>
  createSelector(
    selectProductAllocations(productId),
    (allocations) => {
      if (isEmptyArray(allocations)) {
        return false;
      }
      return allocations.some((a) => !isEmptyArray(a.allocation_qty));
    },
  );

export const selectVariationHasInventoryAllocations = (productId: number, skuId: number) =>
  createSelector(
    selectProductAllocations(productId),
    (allocations) => {
      if (isEmptyArray(allocations)) {
        return false;
      }
      return !!allocations.find((a) => a.sku_id === skuId && !isEmptyArray(a.allocation_qty));
    },
  );

export const selectIsInventoryAllocationOutOfStock = (productId: number, skuId?: number) =>
  createSelector(
    selectInventoryAllocationForSku(productId, skuId),
    (allocation) => allocation.in_stock === false,
  );

export const selectIsProductInventoryAllocationOutOfStock = (productId: number) =>
  createSelector(
    selectProductAllocations(productId),
    (allocations) => {
      if (isEmptyArray(allocations)) {
        return true;
      }
      // if every allocation is out of stock, treat product as out of stock
      return allocations.every((a) => a.in_stock === false);
    },
  );
